/**
 * Rize for Realtor (BDS) team flow
 * --------------------------------
 * A "New Rize for Realtor Team" link behaves like a Satellite / Team link, with these
 * differences (client meeting):
 *  - margins are always entered by the person generating the link
 *    ("Will the ... setup their own Margins?" is locked to No)
 *  - the head of the team is a Business Development Team Lead, not an Area Manager
 *  - the Team Lead has no salary — compensation is override / bonus based, with an Override BPS
 *  - the Team Lead does not originate loans; Loan Officers are assigned to the team instead
 *  - the onboarding form asks for the Team Lead's details without an NMLS #
 */
export const NEW_RIZE_FOR_REALTOR_TEAM = 'New Rize for Realtor Team';

// Links generated before the "Rise" -> "Rize" spelling fix still carry the old _type.
const LEGACY_RISE_FOR_REALTOR_TEAM = 'New Rise for Realtor Team';

export const BDS_TEAM_LEAD_TITLE = 'Business Development Team Lead';

// Titles the head of a Rize for Realtor team can hold. These are configured as
// override-only positions in the title dropdown data, which is the source of truth —
// only the ones actually present there are offered.
export const RIZE_TEAM_LEAD_TITLES = [
    BDS_TEAM_LEAD_TITLE,
    'Business Development Manager',
];

/**
 * All three Business Development positions are override / bonus based — no salary and
 * no compensation rate, just an Override (BPS). Their title dropdown rows are configured
 * that way too ("Outside Sales (Override Only)" with CompensationRateBox off), so the
 * Override (BPS) field follows the title rather than the flow.
 */
export const OVERRIDE_ONLY_TITLES = [
    ...RIZE_TEAM_LEAD_TITLES,
    'Business Development Specialist',
];

export const isOverrideOnlyTitle = (title) => OVERRIDE_ONLY_TITLES.includes(title);

// A Co Team Lead can hold any of the Business Development positions.
export const RIZE_CO_TEAM_LEAD_TITLES = OVERRIDE_ONLY_TITLES;

// Titles offered when a team member is added to a Rize for Realtor team in the
// onboarding form. Existing roles (Processors etc.) stay available to every other flow.
export const RIZE_EMPLOYEE_TITLES = [
    ...RIZE_CO_TEAM_LEAD_TITLES,
    'Loan Officer Outside Sales',
];

// The management titles offered for the Team Lead / Co Team Lead of every other flow.
export const managementTitleOptions = (titles = []) =>
    titles.filter((item) =>
        item.Title && (item.Title.includes('Manager') || item.Title.includes('Team Lead'))
    );

export const isRizeForRealtorFlow = (type) =>
    type === NEW_RIZE_FOR_REALTOR_TEAM || type === LEGACY_RISE_FOR_REALTOR_TEAM;

// Display label for a link type ("New Branch" -> "Branch"). Legacy links still
// stored with the "Rise" spelling are shown as "Rize".
export const flowTypeLabel = (type) =>
    (type || '').replace('New ', '').replace('Rise for Realtor', 'Rize for Realtor');

/**
 * The values a Business Development Team Lead must carry. The link generation API
 * rejects anything else for this flow (a different title, a salary employment type,
 * a compensation rate, or originating loans), so apply them wherever manager details
 * can arrive from another flow.
 */
export const rizeManagerDefaults = (manager) => {
    if (!manager || Object.keys(manager).length === 0) return manager;
    return {
        ...manager,
        // keep a title that is valid for this flow, otherwise fall back to the default
        title: RIZE_TEAM_LEAD_TITLES.includes(manager.title) ? manager.title : BDS_TEAM_LEAD_TITLE,
        isOriginatingLoans: false,
        isShowTransactions: false,
        compensationRate: '',
    };
};

// Loan Officers belong to a Rize for Realtor team only, so they are dropped when the
// link type is switched to another flow. An empty person is left untouched — the form
// checks for that to know whether the details were filled in at all.
export const withoutAssignedLoanOfficers = (person) => {
    if (!person || Object.keys(person).length === 0) return person;
    if (!(person.assigned_loan_officers?.length > 0)) return person;
    return { ...person, assigned_loan_officers: [] };
};
